/* eslint-disable no-undef */
class ConsentManager extends Core {
	constructor() {
		super();
		this.consentModal = null;
		this.daysToExpiry = 60;
		this.consentMode = localStorage.getItem("consentMode");
		this.privacyPageUrl = "/pages/privacy-policy/";
		this.cookiePageUrl = "/pages/cookies/";
		this.rd_UETQ = "uetq"; // will be replaced with rudderstack uetq alternative variable name
	}

	init() {
		if (!this.consentMode) {
			this.consentModal = super.addModal("consent-modal", this.handleUserSelection.bind(this));
			const showPrivacybtn = this.consentModal.querySelector("[data-show-privacy-page]");
			const showCookiebtn = this.consentModal.querySelector("[data-show-cookie-page]");
			showPrivacybtn.addEventListener("click", this.showInfo.bind(this, this.privacyPageUrl));
			showCookiebtn.addEventListener("click", this.showInfo.bind(this, this.cookiePageUrl));
		}

		// Microsoft UET consent
		// Ruddestack includes the bing scripts using a different variable name each time the page
		// is reloaded, we need to find out this name in order to update the consent chosen by the user
		const observer = new MutationObserver((mutationsList) => {
			mutationsList.forEach((mutation) => {
				Array.from(mutation.addedNodes).forEach((node) => {
					// rudderstack will add the script tag containing 'data-ueto="NAME-WE-WANT"'
					if (node.tagName === "SCRIPT" && node.dataset.ueto) {
						this.rd_UETQ = node.dataset.ueto;
						window[this.rd_UETQ] = window[this.rd_UETQ] || [];
						if (this.consentMode) {
							const { ad_storage: adStorage } = JSON.parse(this.consentMode);
							window[this.rd_UETQ].push("consent", "update", {
								ad_storage: adStorage,
							});
						} else {
							window[this.rd_UETQ].push("consent", "default", {
								ad_storage: "denied",
							});
						}
					}
				});
			});
		});
		observer.observe(document.head, { childList: true });
	}

	async showInfo(url, e) {
		e.preventDefault();
		const loading = this.consentModal.querySelector("#loading-privacy");
		loading.classList.remove("hidden");

		const request = await super.asyncRequest({ url, method: "GET" });
		if (request && request.status === 200) {
			const html = request.response;
			const parser = new DOMParser();
			const pageHtml = parser.parseFromString(html, "text/html");
			const pageMain = pageHtml.querySelector("main");
			const pageSection = pageMain.querySelector("section");
			const overlay = document.createElement("div");
			const closeBtn = document.createElement("div");
			closeBtn.innerText = "[Close]";
			closeBtn.setAttribute("data-close-modal", "data-close-modal");
			closeBtn.classList.add("m-12", "text-center", "cursor-pointer", "font-bold", "text-secondary");

			pageSection.classList.add(
				"relative",
				"z-50",
				"w-[940px]",
				"max-w-[95%]",
				"m-[10vh_auto]",
				"overflow-scroll",
				"max-h-[80vh]"
			);
			overlay.classList.add(
				"fixed",
				"inset-0",
				"z-40",
				"bg-black",
				"bg-opacity-70",
				"transition-all",
				"duration-1000",
				"pointer-events-none"
			);

			pageSection.prepend(closeBtn);
			const closeBtn2 = closeBtn.cloneNode(true);
			pageSection.appendChild(closeBtn2);
			pageMain.appendChild(overlay);
			super.addModal(pageMain, this.init.bind(this));
		}
	}

	handleUserSelection({ currentTarget }) {
		const chosenOption = currentTarget.getAttribute("id");
		switch (chosenOption) {
			case "accept-all":
				this.acceptAll();
				break;
			case "accept-selection":
				this.acceptSome();
				break;
			default:
				this.rejectAll();
				break;
		}
	}

	setConsent(consent) {
		const consentMode = {
			functionality_storage: consent.necessary ? "granted" : "denied",
			security_storage: consent.necessary ? "granted" : "denied",
			ad_storage: consent.marketing ? "granted" : "denied",
			analytics_storage: consent.analytics ? "granted" : "denied",
			personalization_storage: consent.preferences ? "granted" : "denied",
		};

		// GTM consent
		gtag("consent", "update", consentMode);
		gtag("event", "consentUpdated");

		// Microsoft UET consent
		window[this.rd_UETQ] = window[this.rd_UETQ] || [];
		window[this.rd_UETQ].push("consent", "update", consentMode);

		// Rudderstack consent
		if (window.rudderanalytics) {
			const granted = [];
			const denied = [];
			Object.entries(consentMode).forEach(([k, v]) => {
				if (v === "granted") {
					granted.push(k);
				} else {
					denied.push(k);
				}
			});
			window.rudderanalytics.consent({
				consentManagement: {
					allowedConsentIds: granted,
					deniedConsentIds: denied,
				},
				trackConsent: true,
				discardPreConsentEvents: true,
				storage: {
					type: "localStorage",
				},
			});
		}
		const expiryDate = Date.now() + this.daysToExpiry * 24 * 60 * 60 * 1000; // Days * Hours * Minutes * Seconds * Milliseconds
		localStorage.setItem("consentMode", JSON.stringify(consentMode));
		localStorage.setItem("consentModeExpiry", JSON.stringify(expiryDate));
	}

	acceptAll() {
		this.setConsent({
			necessary: true,
			analytics: true,
			preferences: true,
			marketing: true,
		});
	}

	acceptSome() {
		const consentInputs = Array.from(this.consentModal.querySelectorAll("input"));
		const consent = {};
		consentInputs.forEach((i) => {
			consent[i.name] = i.checked;
		});
		this.setConsent(consent);
	}

	rejectAll() {
		this.setConsent({
			necessary: true,
			analytics: false,
			preferences: false,
			marketing: false,
		});
	}
}

const consentBanner = new ConsentManager();
consentBanner.init();
